/**
 * 菜单工具函数
 */
import type { MenuItem } from '@/types/menu';

/**
 * 递归标记第4级菜单
 * 第1级: 顶级菜单 - 左侧sidebar显示
 * 第2级: 二级菜单 - 顶部navbar或侧边栏显示
 * 第3级: 三级菜单 - 侧边栏collapse显示
 * 第4级: 四级菜单 - 内容区tab显示
 * @param menus 菜单列表
 * @param level 当前层级，默认为1
 */
export function markFourthLevelMenu(menus: MenuItem[], level: number = 1): void {
  menus.forEach((menu) => {
    // 如果有子菜单
    if (menu.children && menu.children.length > 0) {
      // 如果是第3级且有子菜单，则子菜单为第4级，需要标记为tab菜单
      if (level === 3) {
        menu.children.forEach((child) => {
          if (!child.meta) {
            child.meta = {};
          }
          child.meta.isTabsMenu = true; // 标记为tab菜单
        });
      }
      // 递归处理子菜单
      markFourthLevelMenu(menu.children, level + 1);
    }
  });
}

/**
 * 查找菜单项通过路径
 * @param menus 菜单列表
 * @param path 目标路径
 * @returns 找到的菜单项或null
 */
export function findMenuByPath(menus: MenuItem[], path: string): MenuItem | null {
  for (const menu of menus) {
    if (menu.path === path) {
      return menu;
    }
    if (menu.children && menu.children.length > 0) {
      const found = findMenuByPath(menu.children, path);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * 获取菜单的层级深度
 * @param menus 菜单列表
 * @param targetPath 目标路径
 * @param currentLevel 当前层级
 * @returns 层级深度，找不到返回-1
 */
export function getMenuLevel(
  menus: MenuItem[],
  targetPath: string,
  currentLevel: number = 1
): number {
  for (const menu of menus) {
    if (menu.path === targetPath) {
      return currentLevel;
    }
    if (menu.children && menu.children.length > 0) {
      const level = getMenuLevel(menu.children, targetPath, currentLevel + 1);
      if (level !== -1) {
        return level;
      }
    }
  }
  return -1;
}

/**
 * 获取父级菜单
 * @param menus 菜单列表
 * @param targetPath 目标路径
 * @param parent 父级菜单
 * @returns 父级菜单或null
 */
export function getParentMenu(
  menus: MenuItem[],
  targetPath: string,
  parent: MenuItem | null = null
): MenuItem | null {
  for (const menu of menus) {
    if (menu.path === targetPath) {
      return parent;
    }
    if (menu.children && menu.children.length > 0) {
      const found = getParentMenu(menu.children, targetPath, menu);
      if (found !== null) {
        return found;
      }
      // 如果子菜单中直接包含目标路径，返回当前菜单作为父级
      if (menu.children.some((c) => c.path === targetPath)) {
        return menu;
      }
    }
  }
  return null;
}

/**
 * 根据菜单路径生成面包屑数据
 * @param menus 菜单列表
 * @param targetPath 目标路径
 * @returns 面包屑数组
 */
export function generateBreadcrumbs(menus: MenuItem[], targetPath: string): Array<{
  path: string; meta: { title: string }
}> {
  const breadcrumbs: Array<{ path: string; meta: { title: string } }> = [];

  // 递归查找路径并记录路径上的所有菜单
  const findPath = (menuList: MenuItem[], path: string, ancestors: MenuItem[] = []): boolean => {
    for (const menu of menuList) {
      if (menu.path === path) {
        // 找到目标，将所有祖先菜单和当前菜单加入面包屑
        [...ancestors, menu].forEach((item) => {
          breadcrumbs.push({
            path: item.path,
            meta: {
              title: item.title || '',
            },
          });
        });
        return true;
      }
      if (menu.children && menu.children.length > 0) {
        if (findPath(menu.children, path, [...ancestors, menu])) {
          return true;
        }
      }
    }
    return false;
  };

  findPath(menus, targetPath);
  return breadcrumbs;
}
